// CSV / JSON input parsing with header aliases (spec §8.3). Stand-in for the core's format/csv.ts.
import type { InputRow } from './engine';

/** RFC 4180 parser: BOM, CRLF, quoted commas/newlines, doubled quotes. */
export function parseCsv(text: string): string[][] {
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  const rows: string[][] = [];
  let row: string[] = [], cell = '', q = false, i = 0;
  const n = text.length;
  while (i < n) {
    const ch = text[i];
    if (q) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 2; continue; }
        q = false; i++; continue;
      }
      cell += ch; i++; continue;
    }
    if (ch === '"') { q = true; i++; continue; }
    if (ch === ',') { row.push(cell); cell = ''; i++; continue; }
    if (ch === '\r' || ch === '\n') {
      row.push(cell); cell = '';
      if (!(row.length === 1 && row[0] === '')) rows.push(row);
      row = [];
      i += ch === '\r' && text[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    cell += ch; i++;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

const ALIASES: Record<string, keyof InputRow> = {
  url: 'url', address: 'url', 'page url': 'url', loc: 'url',
  title: 'title', 'title 1': 'title', 'title tag': 'title', 'page title': 'title', 'seo title': 'title',
  description: 'description', 'meta description 1': 'description', 'meta description': 'description',
  brand: 'brand',
  target_keyword: 'targetKeyword', 'target keyword': 'targetKeyword', 'focus keyword': 'targetKeyword', keyword: 'targetKeyword',
  query: 'query', 'search query': 'query',
  error: 'error',
};

export class InputError extends Error {}

export function rowsFromCsv(text: string): InputRow[] {
  const rows = parseCsv(text);
  if (!rows.length) throw new InputError('The file is empty.');
  const header = rows[0].map(h => h.trim());
  const keys = header.map(h => ALIASES[h.toLowerCase()]);
  if (!keys.includes('title') && !keys.includes('description')) {
    throw new InputError(`No title or description column found. Columns found: ${header.filter(Boolean).join(', ') || '(none)'}.`);
  }
  const out: InputRow[] = [];
  for (let r = 1; r < rows.length; r++) {
    const o: InputRow = {};
    rows[r].forEach((v, i) => { const k = keys[i]; if (k && !o[k]) o[k] = v; });
    out.push(o);
  }
  return out;
}

export function rowsFromJson(text: string): InputRow[] {
  let data: unknown;
  const t = text.trim();
  try { data = JSON.parse(t); } catch {
    // NDJSON
    data = t.split(/\r?\n/).filter(Boolean).map((l, i) => {
      try { return JSON.parse(l); } catch { throw new InputError(`Line ${i + 1} is not valid JSON.`); }
    });
  }
  const arr = Array.isArray(data) ? data : (data as any)?.rows;
  if (!Array.isArray(arr)) throw new InputError('JSON must be an array of rows or {"rows": [...]}.');
  return arr.filter(o => o && typeof o === 'object' && !('run' in o && Object.keys(o).length === 1)).map((o: any) => {
    const row: InputRow = {};
    for (const [k, v] of Object.entries(o)) {
      const key = ALIASES[k.toLowerCase()] ?? (k.toLowerCase() === 'targetkeyword' ? 'targetKeyword' : undefined);
      if (key && typeof v === 'string') row[key] = v;
    }
    return row;
  });
}

export function parseInput(text: string, fileName = ''): InputRow[] {
  const t = text.trimStart().replace(/^﻿/, '');
  if (/\.(json|ndjson)$/i.test(fileName) || t.startsWith('[') || t.startsWith('{')) return rowsFromJson(t);
  return rowsFromCsv(text);
}
